import {Injectable} from '@angular/core';
import {TUI_RUSSIAN_LANGUAGE} from '@taiga-ui/i18n';
import {AmountCartMethod, LoginBy, RecoverPasswordBy, SignUpBy} from '../vars';

@Injectable({
  providedIn: 'root'
})
export abstract class Environment {
  abstract isOpenCabinet: boolean;
  abstract serverApi: string;
  abstract serverUrl: string;

  /** Origin (scheme://host[:port]) собственного бэка — для same-origin проверок. */
  get apiOrigin(): string {
    return new URL(this.serverApi).origin;
  }

  production = false;
  accessRolesCatalog = false;
  serviceWorkers = false;
  serverPicturesPath = 'http://localhost:8086/pictures';
  // Резервный хост картинок для тестовых окружений: при ошибке загрузки с
  // serverPicturesPath ImageFallbackService делает одну повторную попытку отсюда.
  // Пусто (по умолчанию и в prod) — фича выключена, слушатель не вешается.
  serverPicturesPathFallback = '';
  serverDocumentsPath = 'http://localhost:8086/documents';
  youTubeShortLink = 'https://i.ytimg.com/vi/';
  companyName = 'Compo';

  codeField = 'codes.elcom';
  tokenName = 'Bearer';
  visibleSelectTagCount = 5;
  toastrNotificationDuration = 5000;
  gridPaginationRange = 25;
  dateFormat = 'dd.MM.yyyy';
  dadataToken = '6176f37b5bc4b5ff40f82e3108a4f9300a1d108e';
  originalsImagesFolder = 'originals';
  documentFolder = 'documents';
  locale = 'ru';
  /**
   * Состав контент-локалей приложения (язык ДАННЫХ/справочников). Источник правды
   * для списка локалей во всём фронте (через {@code setSupportedLocales} → {@code SUPPORTED_LOCALES}).
   * Дефолт {@code ['ru','en']} = прежнее поведение; добавить язык (напр. kk) = правка
   * этого поля в {@code environment.<стенд>.ts}, без правки кода (зеркало бэкендового
   * {@code compob2b.translation.target-locales}). Канон {@code 'ru'} гарантируется автоматически.
   */
  contentLocales: string[] = ['ru', 'en'];
  tuiLanguage = TUI_RUSSIAN_LANGUAGE;
  amountCartMethod = AmountCartMethod.UNIQUE;
  useProductTemplateFeatures = true;
  useYandexGPT = true;
  damTemplateId = 411;
  damMaxFileSize = 4 * 1024 * 1024 * 1024;
  damNewPermissions = false;
  damChangeLog = true;
  featureGroupPermissions = false;
  masterCardSystemTagId = 100;
  enableDescriptionGenerateAiButton = true;
  showDiscountPanel = false;
  companyTypes = ['Юр. лицо', 'ИП'];
  loginBy = LoginBy.PHONE;
  impersonate = true;
  // Корень, а не `/cabinet/dashboard` (SEO-8): главная кабинета переехала в корень.
  // Иначе токен impersonate ехал бы через лишний 301 — работает, но секрет
  // незачем гонять по редиректам. `cabinetHost` выводится отсюда парсингом URL,
  // поэтому origin от смены пути не меняется.
  impersonatePortalPath = 'http://localhost:4200/';
  flatCodeAnalogAndRelated = 'main';
  recoverPasswordBy = RecoverPasswordBy.SMS; // email or sms
  maxHtmlContentLength = 300;
  maxTextAreaLength = 200;
  maxInputLength = 50;
  enableWorkflowButton = false;
  /** Включает раздел «Публичная витрина» в админке. По умолчанию выключено. */
  publicShowcase = false;
  /**
   * Включает фичи ИИ-переводов PIM: меню «ИИ-переводы», переключатель языка RU/EN,
   * кнопки перевода на карточке товара и массовую операцию «Переводы».
   * По умолчанию выключено.
   */
  aiTranslation = false;
  /**
   * PIM2-449: показывать в листинге товаров колонку «Заполненность карточки»,
   * фильтр по проценту заполнения и быстрые чипы. По умолчанию выключено.
   */
  completenessEnabled = false;
  /**
   * PIM2-580: показывает контрол «значение по умолчанию» у характеристик шаблона и открывает
   * окно прогресса массового backfill при сохранении шаблона. По умолчанию выключено
   * (синхронно с бэкенд-гейтом compob2b.template-defaults.enabled — включать на деплое парно).
   */
  templateDefaultsEnabled = false;
  /**
   * Включает раздел «Валидатор изображений» в админке (меню PIM → /cabinet/pim/image-validator).
   * Проверка загружаемых изображений товаров (формат, размер, прозрачность) перед обработкой.
   * По умолчанию выключено.
   */
  imageValidator = false;
  /**
   * PIM2-579: показывает в форме поставщицкого шаблона переключатель формата Excel/XML и
   * инструменты XML-разметки (выбор якоря-товара по anchor-candidates → set-anchor → маппинг).
   * По умолчанию выключено (парно с бэкенд-гейтом compob2b.xml-import.enabled).
   */
  xmlImport = false;
  /**
   * PIM2-637: показывает чекбокс «Общая группа» в карточке группы характеристик (только для
   * ROLE_SUPER_ADMIN). Флаг общности хранится прямо на группе (`feature_group.is_common`,
   * колонка демо на CORE-таблице) — отдельного эндпоинта настроек нет.
   * По умолчанию выключено (парно с бэкенд-гейтом `compob2b.common-feature-group.enabled`,
   * независимые гейты — оба должны быть включены).
   */
  commonFeatureGroup = false;

  get cabinetHost() {
    const url = new URL(this.impersonatePortalPath);
    return `${url.protocol}//${url.host}`;
  }
}

export class EnvironmentAdmin extends Environment {
  isOpenCabinet = false;
  serverApi = 'http://localhost:8086/api/v1';
  serverUrl = 'http://localhost:8086';
  publicShowcaseUrl = 'http://localhost:4401';
}

export class EnvironmentCabinet extends Environment {
  isOpenCabinet = true;
  serverApi = 'http://localhost:8080/api/v1';
  serverUrl = 'http://localhost:8080';
  notificationCustomerAccess = true;
  allowAnonymousView = true;
  loadedRoutes = {
    // Default is SignUpBy.EMAIL and login by email, 2 steps registration form, no sms phone verification required
    signupFormType: SignUpBy.EMAIL
  };
  showcaseUrl = '';

}
